import { format, formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale/sv';
import { enUS } from 'date-fns/locale/en-US';
import { DEFAULT_LOCALE, type SupportedLocale } from '@connexio/shared/constants';

const DATE_LOCALES = { en: enUS, sv } as const;

const INTL_LOCALE_MAP: Record<SupportedLocale, string> = {
  en: 'en-US',
  sv: 'sv-SE',
};

/**
 * Date-only values (`2016-10-18`, or the same day as midnight UTC, which is how the API
 * serialises date columns) are calendar days, not instants. Parsing them with `new Date()`
 * gives UTC midnight, which shows as the previous day west of UTC, so read them as local days.
 * Only output without a time may do this: a real timestamp at 00:00Z (a task due 20:00 in
 * New York) must stay an instant. A bare `yyyy-MM-dd` has no time, so it is always a local day.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})(T00:00:00(?:\.000)?Z)?$/;
const TIME_TOKENS = /[HhKkmsSaBbpXxZz]/;

function toDate(value: Date | string | number, pattern: string): Date {
  if (typeof value === 'string') {
    const m = DATE_ONLY.exec(value);
    if (m && (!m[4] || !TIME_TOKENS.test(pattern))) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
  }
  return typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
}

export function formatDate(
  date: Date | string | number,
  pattern: string,
  locale: SupportedLocale,
): string {
  return format(toDate(date, pattern), pattern, { locale: DATE_LOCALES[locale] ?? enUS });
}

/** App-wide calendar date (yyyy-MM-dd). The pattern is locale-independent, so `locale` is optional. */
export function formatAppDate(value: Date | string | number, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return formatDate(value, 'yyyy-MM-dd', locale);
}

/** App-wide timestamp (yyyy-MM-dd HH:mm) in the viewer's local time zone. */
export function formatAppDateTime(value: Date | string | number, locale: SupportedLocale = DEFAULT_LOCALE): string {
  return formatDate(value, 'yyyy-MM-dd HH:mm', locale);
}

export function formatRelativeTime(
  date: Date | string | number,
  locale: SupportedLocale,
): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return formatDistanceToNow(d, {
    addSuffix: true,
    locale: DATE_LOCALES[locale] ?? enUS,
  });
}

export function formatCurrency(
  value: number,
  currency: string,
  locale: SupportedLocale = DEFAULT_LOCALE,
  /** `trimWholeAmounts`: whole amounts without decimals ("1 200 kr"); fractions keep two ("249,50 kr"). */
  options?: { trimWholeAmounts?: boolean },
): string {
  return new Intl.NumberFormat(INTL_LOCALE_MAP[locale] ?? 'en-US', {
    style: 'currency',
    currency,
    ...(options?.trimWholeAmounts && Number.isInteger(value) ? { minimumFractionDigits: 0 } : {}),
  }).format(value);
}

export function formatNumber(
  value: number,
  locale: SupportedLocale,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(INTL_LOCALE_MAP[locale] ?? 'en-US', options).format(value);
}
